"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CheckAll = exports.Checkbox = exports.CheckboxGroup = void 0;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var InternalCheckbox_1 = require("./InternalCheckbox");
var ConfigProvider_1 = require("../ConfigProvider");
var utils_1 = require("../utils");
var hooks_1 = require("../utils/hooks");
var groupContext = react_1.createContext(null);
function CheckboxGroup(_a) {
    var valueFromProps = _a.value, _b = _a.defaultValue, defaultValue = _b === void 0 ? [] : _b, disabled = _a.disabled, onChange = _a.onChange, size = _a.size, children = _a.children;
    var _c = hooks_1.useValue({ valueFromProps: valueFromProps, defaultValue: defaultValue, onChange: onChange }), value = _c.value, setValue = _c.setValue;
    var _d = react_1.useState([]), options = _d[0], setOptions = _d[1];
    var registerOption = react_1.useCallback(function (val, dis) {
        if (dis === void 0) { dis = false; }
        setOptions(function (prevOptions) {
            var existed = prevOptions.find(function (option) { return option.value === val; });
            if (existed) {
                existed.disabled = dis;
                return prevOptions;
            }
            return tslib_1.__spreadArrays(prevOptions, [{ value: val, disabled: dis }]);
        });
    }, []);
    var removeOption = react_1.useCallback(function (val) {
        setOptions(function (prevOptions) { return prevOptions.filter(function (v) { return v.value !== val; }); });
    }, []);
    var toggleCheck = react_1.useCallback(function (val) {
        var valueIndex = value.indexOf(val);
        var newValue = tslib_1.__spreadArrays(value);
        if (valueIndex === -1) {
            newValue.push(val);
        }
        else {
            newValue.splice(valueIndex, 1);
        }
        var sortedValue = options.filter(function (o) { return newValue.includes(o.value); }).map(function (o) { return o.value; });
        setValue(sortedValue);
    }, [options, setValue, value]);
    var toggleCheckAll = react_1.useCallback(function (check) {
        var filteredOptions = check
            ? options.filter(function (o) { return (o.disabled ? value.includes(o.value) : true); })
            : options.filter(function (o) { return o.disabled && value.includes(o.value); });
        var newValue = filteredOptions.map(function (o) { return o.value; });
        setValue(newValue);
    }, [options, setValue, value]);
    var context = react_1.useMemo(function () { return ({
        value: value,
        disabled: disabled,
        total: options.length,
        size: size,
        registerOption: registerOption,
        removeOption: removeOption,
        toggleCheck: toggleCheck,
        toggleCheckAll: toggleCheckAll
    }); }, [disabled, options.length, registerOption, removeOption, size, toggleCheck, toggleCheckAll, value]);
    return (react_1.default.createElement(groupContext.Provider, { value: context }, children));
}
exports.CheckboxGroup = CheckboxGroup;
function Checkbox(_a) {
    var _b;
    var _c;
    var bordered = _a.bordered, value = _a.value, restProps = tslib_1.__rest(_a, ["bordered", "value"]);
    var prefix = ConfigProvider_1.usePrefix('checkbox');
    var checkboxProps = tslib_1.__assign({}, restProps);
    var checkboxGroup = react_1.useContext(groupContext);
    if (!checkboxGroup) {
        checkboxProps.className = classnames_1.default((_b = {},
            _b[utils_1.concat(prefix, 'bordered')] = bordered,
            _b), restProps.className);
    }
    else {
        if (utils_1.isDev && value === undefined) {
            // eslint-disable-next-line no-console
            console.warn('`value` must be set when `<Checkbox>` used in `<CheckboxGroup>`');
        }
        checkboxProps.onChange = function (checked) {
            var _a;
            (_a = restProps.onChange) === null || _a === void 0 ? void 0 : _a.call(restProps, checked);
            checkboxGroup.toggleCheck(value);
        };
        checkboxProps.checked = checkboxGroup.value.includes(value);
        checkboxProps.disabled = restProps.disabled || checkboxGroup.disabled;
        checkboxProps.size = (_c = checkboxProps.size) !== null && _c !== void 0 ? _c : checkboxGroup.size;
    }
    var registerOption = checkboxGroup === null || checkboxGroup === void 0 ? void 0 : checkboxGroup.registerOption;
    var removeOption = checkboxGroup === null || checkboxGroup === void 0 ? void 0 : checkboxGroup.removeOption;
    react_1.useEffect(function () {
        registerOption === null || registerOption === void 0 ? void 0 : registerOption(value, checkboxProps.disabled);
        return function () { return removeOption === null || removeOption === void 0 ? void 0 : removeOption(value); };
    }, [value, checkboxProps.disabled, registerOption, removeOption]);
    return react_1.default.createElement(InternalCheckbox_1.InternalCheckbox, tslib_1.__assign({}, checkboxProps));
}
exports.Checkbox = Checkbox;
function CheckAll(props) {
    var _a, _b;
    var _c = react_1.useState(false), checked = _c[0], setChecked = _c[1];
    var _d = react_1.useState(false), partialChecked = _d[0], setPartialChecked = _d[1];
    var checkboxGroup = react_1.useContext(groupContext);
    var total = checkboxGroup === null || checkboxGroup === void 0 ? void 0 : checkboxGroup.total;
    var count = (_a = checkboxGroup === null || checkboxGroup === void 0 ? void 0 : checkboxGroup.value) === null || _a === void 0 ? void 0 : _a.length;
    react_1.useEffect(function () {
        if (count === undefined || total === undefined) {
            return;
        }
        if (count === 0) {
            setChecked(false);
        }
        else if (count === total) {
            setChecked(true);
        }
        setPartialChecked(count > 0 && count < total);
    }, [total, count]);
    var checkboxProps = tslib_1.__assign({}, props);
    if (checkboxGroup) {
        checkboxProps.onChange = function (check) {
            var _a;
            setChecked(check);
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, check);
            checkboxGroup.toggleCheckAll(check);
        };
        checkboxProps.disabled = props.disabled || checkboxGroup.disabled;
    }
    var checkAllText = ConfigProvider_1.useLangData('checkbox').checkAllText;
    checkboxProps.children = (_b = props.children) !== null && _b !== void 0 ? _b : checkAllText;
    return react_1.default.createElement(InternalCheckbox_1.InternalCheckbox, tslib_1.__assign({}, checkboxProps, { checked: checked, partialChecked: partialChecked }));
}
exports.CheckAll = CheckAll;
//# sourceMappingURL=index.js.map